import { useEffect, useState } from 'react';
import { getToken, getTokenContent } from '../../../utils/token';
import { fetchUserByUuid } from '../../../services/userService';

interface UseInsightsThreadOwnerOptions {
  ownerAuthServiceUuid: string | null;
}

interface UseInsightsThreadOwnerResult {
  isViewingOtherUsersThread: boolean;
  threadOwnerEmail: string | null;
}

export const useInsightsThreadOwner = ({
  ownerAuthServiceUuid,
}: UseInsightsThreadOwnerOptions): UseInsightsThreadOwnerResult => {
  const currentUserUuid = getTokenContent()?.payload.user.uuid ?? null;
  const [threadOwnerEmail, setThreadOwnerEmail] = useState<string | null>(null);

  const isViewingOtherUsersThread: boolean =
    !!ownerAuthServiceUuid && !!currentUserUuid && ownerAuthServiceUuid !== currentUserUuid;

  useEffect(() => {
    if (!isViewingOtherUsersThread || !ownerAuthServiceUuid) {
      setThreadOwnerEmail(null);
      return;
    }

    const abortController = new AbortController();

    const fetchOwner = async (): Promise<void> => {
      try {
        const token = getToken();
        if (!token) {
          setThreadOwnerEmail(null);
          return;
        }

        const user = await fetchUserByUuid(ownerAuthServiceUuid, token, abortController.signal);
        setThreadOwnerEmail(user.emailAddress ?? null);
      } catch (error: unknown) {
        if (!abortController.signal.aborted) {
          console.error('Error fetching thread owner:', error);
          setThreadOwnerEmail(null);
        }
      }
    };

    void fetchOwner();

    return () => {
      abortController.abort();
    };
  }, [isViewingOtherUsersThread, ownerAuthServiceUuid]);

  return {
    isViewingOtherUsersThread,
    threadOwnerEmail,
  };
};
